import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RestaurantOrderService } from '../../services/restaurant-order.service';
import { GlobalService } from '../../services/global.service';

type SettlementMode = 'single' | 'equal';
type SettlementStep = 'choose' | 'setup' | 'review' | 'payment';

interface RestaurantSettleBillDialogData {
  restaurantId: string;
  orderId: string;
  tableNumber: string;
  guestName: string;
  orderVersion: string;
  totals: { netCents: number; taxCents: number; tipCents: number; totalCents: number };
  currencyLabel: string;
}

@Component({
  selector: 'app-restaurant-settle-bill-dialog',
  templateUrl: './restaurant-settle-bill-dialog.component.html',
  styleUrls: ['./restaurant-settle-bill-dialog.component.scss'],
})
export class RestaurantSettleBillDialogComponent implements OnInit {
  step: SettlementStep = 'choose';
  mode: SettlementMode = 'single';
  participantCount = 2;
  split: any = null;
  loading = true;
  saving = false;
  guestCountValid = true;
  updatingInvoiceId = '';
  recoveringConflict = false;
  errorMessage = '';
  noticeMessage = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RestaurantSettleBillDialogData,
    private dialogRef: MatDialogRef<RestaurantSettleBillDialogComponent>,
    private restaurantOrderService: RestaurantOrderService,
    private globalService: GlobalService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSplit();
  }

  get equalShareCents(): number {
    return Math.floor(this.data.totals.totalCents / this.participantCount);
  }

  get stepLabel(): string {
    switch (this.step) {
      case 'setup':
        return this.translate.instant('restaurantSplitBill.splitEqually');
      case 'review':
        return this.translate.instant('restaurantSplitBill.confirmPayment');
      case 'payment':
        return this.translate.instant('restaurantSplitBill.collectPayments');
      default:
        return this.translate.instant('restaurantSplitBill.choosePayment');
    }
  }

  continueFromChoice(): void {
    this.step = this.mode === 'single' ? 'review' : 'setup';
  }

  setParticipantCount(value: number): void {
    const next = Number(value);
    if (!Number.isInteger(next) || next < 2 || next > 20) {
      this.guestCountValid = false;
      this.errorMessage = this.translate.instant('restaurantSplitBill.invalidGuestCount');
      return;
    }
    this.guestCountValid = true;
    this.errorMessage = '';
    this.participantCount = next;
  }

  confirmSplit(): void {
    this.createSplit('equal', () => {
      this.step = 'payment';
    });
  }

  markWholeBillPaid(): void {
    this.createSplit('single', () => this.setInvoicePaid(this.split.invoices[0], true));
  }

  setInvoicePaid(invoice: any, paid: boolean): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.updatingInvoiceId = invoice.invoiceId;
    this.restaurantOrderService
      .updateSplitInvoicePaymentStatus({
        ResturantId: this.data.restaurantId,
        requestId: this.data.orderId,
        invoiceId: invoice.invoiceId,
        paid,
      })
      .subscribe(
        (response: any) => {
          this.saving = false;
          this.updatingInvoiceId = '';
          if (!response?.Status) {
            this.handleError(response);
            return;
          }
          this.applySplit(response.Data);
          this.step = 'payment';
        },
        (error) => {
          this.saving = false;
          this.updatingInvoiceId = '';
          this.handleError(error);
        }
      );
  }

  startOver(): void {
    if (!this.split || this.saving || !this.split.canAbandon) {
      return;
    }
    if (!window.confirm(this.translate.instant('restaurantSplitBill.voidConfirmation'))) {
      return;
    }
    this.saving = true;
    this.restaurantOrderService
      .abandonBillSplit({
        ResturantId: this.data.restaurantId,
        requestId: this.data.orderId,
      })
      .subscribe(
        (response: any) => {
          this.saving = false;
          if (!response?.Status) {
            this.handleError(response);
            return;
          }
          this.dialogRef.close();
        },
        (error) => {
          this.saving = false;
          this.handleError(error);
        }
      );
  }

  back(): void {
    this.errorMessage = '';
    this.step = 'choose';
  }

  get isSettled(): boolean {
    return this.split?.status === 'completed';
  }

  close(): void {
    if (this.isSettled) {
      this.globalService.handleSuccessMessage(
        this.translate.instant('restaurantSplitBill.paymentReceived')
      );
    }
    this.dialogRef.close({ completed: this.isSettled });
  }

  money(cents: number): string {
    return (Number(cents || 0) / 100).toFixed(2);
  }

  currencyAmount(cents: number): string {
    return `${this.data.currencyLabel}&nbsp;${this.money(cents)}`;
  }

  guestLabel(position: number): string {
    if (this.split?.mode === 'single') {
      return this.translate.instant('restaurantSplitBill.totalBill');
    }
    const primaryGuestName = String(this.data.guestName || '').trim();
    return position === 1 && primaryGuestName
      ? primaryGuestName
      : this.translate.instant('restaurantSplitBill.guestNumber', { position });
  }

  private loadSplit(showLoading = true, recoveredFromConflict = false): void {
    if (showLoading) {
      this.loading = true;
    }
    this.errorMessage = '';
    if (!recoveredFromConflict) {
      this.noticeMessage = '';
    }
    this.restaurantOrderService
      .getBillSplit({
        ResturantId: this.data.restaurantId,
        requestId: this.data.orderId,
      })
      .subscribe(
        (response: any) => {
          this.loading = false;
          this.saving = false;
          this.updatingInvoiceId = '';
          if (!response?.Status) {
            this.handleError(response);
            return;
          }
          if (response.Data) {
            this.applySplit(response.Data);
            this.step = 'payment';
          } else if (!recoveredFromConflict) {
            this.step = 'choose';
          }
          this.finishConflictRecovery(recoveredFromConflict);
        },
        (error) => {
          this.loading = false;
          this.saving = false;
          this.updatingInvoiceId = '';
          this.recoveringConflict = false;
          this.handleError(error);
        }
      );
  }

  private createSplit(mode: SettlementMode, onCreated: () => void): void {
    if (this.saving || !this.guestCountValid) {
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    this.noticeMessage = '';
    this.restaurantOrderService
      .createBillSplit({
        ResturantId: this.data.restaurantId,
        requestId: this.data.orderId,
        orderVersion: this.data.orderVersion,
        mode,
        participantCount: this.participantCount,
      })
      .subscribe(
        (response: any) => {
          this.saving = false;
          if (!response?.Status) {
            this.handleError(response);
            return;
          }
          this.applySplit(response.Data);
          onCreated();
        },
        (error) => {
          this.saving = false;
          this.handleError(error);
        }
      );
  }

  private applySplit(split: any): void {
    this.split = split;
    this.mode = split.mode;
    this.participantCount = split.participantCount;
    this.guestCountValid = true;
  }

  private handleError(error: any): void {
    const code = this.errorCode(error);
    if (
      !this.recoveringConflict &&
      (code === 'ORDER_CHANGED' || code === 'ACTIVE_BILL_SPLIT')
    ) {
      this.recoverFromConflict();
      return;
    }
    this.showError(error);
  }

  private showError(error: any): void {
    this.errorMessage =
      error?.error?.Message ||
      error?.Message ||
      this.translate.instant('restaurantSplitBill.updateFailed');
    this.globalService.handleApiError(error);
  }

  private errorCode(error: any): string {
    const payload = error?.error || error;
    return Array.isArray(payload?.Errors) ? String(payload.Errors[0]?.Code || '') : '';
  }

  private recoverFromConflict(): void {
    this.recoveringConflict = true;
    this.loading = true;
    this.saving = false;
    this.updatingInvoiceId = '';
    this.errorMessage = '';
    this.noticeMessage = '';

    this.restaurantOrderService
      .getOrder({
        ResturantId: this.data.restaurantId,
        requestId: this.data.orderId,
      })
      .subscribe(
        (response: any) => {
          const order = Array.isArray(response?.Data) ? response.Data[0] : response?.Data;
          if (!response?.Status || !order) {
            this.loading = false;
            this.recoveringConflict = false;
            this.showError(response);
            return;
          }
          this.applyOrderDetail(order);
          this.loadSplit(true, true);
        },
        (error) => {
          this.loading = false;
          this.recoveringConflict = false;
          this.showError(error);
        }
      );
  }

  private applyOrderDetail(order: any): void {
    this.data.orderVersion = String(order.BillVersion || '');
    this.data.guestName = String(order.guestName || '');
    this.data.totals = order.BillTotals;
  }

  private finishConflictRecovery(recoveredFromConflict: boolean): void {
    if (!recoveredFromConflict) {
      return;
    }
    this.recoveringConflict = false;
    this.noticeMessage = this.translate.instant('restaurantSplitBill.billRefreshed');
  }
}
